import { useCallback, useMemo } from 'react';

import { ConsumerStudentFee, Student } from '@givebacks/api/student';
import { ClickableOptionsCard } from 'core/Components';
import { DateTime } from 'luxon';
import { ButtonMantine, Divider, Stack, Text, TextProps, centsToDollar } from 'ui';
import { IconCheck } from 'ui/Icons';

import { useIsFeeInCart } from '../../../../hooks/useIsFeeInCart';
import { useStudentName } from '../../../../hooks/useStudentName';
import { AddFeeToCartButton } from '../../../organisms/buttons/AddFeeToCartButton';
import { FeeImage } from './FeeImage';

type FeeDisplayCardPropsBase = {
  studentFee: ConsumerStudentFee;
  onClick: (feeId: string) => void;
  student?: Student;
  withStudentName?: boolean;
};

type FeeDisplayCardPropsBaseWithStudentName = FeeDisplayCardPropsBase & {
  student?: Student;
  withStudentName: true;
};

type FeeDisplayCardPropsBaseWithoutStudentName = FeeDisplayCardPropsBase & {
  withStudentName?: never;
};

type FeeDisplayCardProps =
  | FeeDisplayCardPropsBaseWithStudentName
  | FeeDisplayCardPropsBaseWithoutStudentName;

interface FeeTextProps extends TextProps {
  children: React.ReactNode;
}

const FeeText = ({ children, ...props }: FeeTextProps) => {
  return (
    <Text size="sm" c="gray.6" {...props}>
      {children}
    </Text>
  );
};

const FeeDueDate = ({ dueAt }: { dueAt?: string }) => {
  const dueDate = useMemo(() => {
    if (!dueAt) return undefined;
    return DateTime.fromISO(dueAt);
  }, [dueAt]);

  const isDue = useMemo(() => {
    if (!dueDate) return false;
    return DateTime.now().diff(dueDate).toMillis() > 0;
  }, [dueDate]);

  if (!dueDate) return null;

  return <FeeText c={isDue ? 'cerise.6' : 'gray.6'}>Due: {dueDate.toFormat('MM/dd/yyyy')}</FeeText>;
};

const FeeOptional = ({ optional }: { optional: boolean }) => {
  if (!optional) return null;
  return <FeeText>Optional</FeeText>;
};

const FeePartialPayment = ({ allowPartialPayment }: { allowPartialPayment: boolean }) => {
  if (!allowPartialPayment) return null;
  return <FeeText>Partial Payments</FeeText>;
};

const FeeStudentName = ({ student }: { student: Student }) => {
  const studentName = useStudentName(student);
  return <FeeText>{studentName}</FeeText>;
};

const FeeInfo = ({
  studentFee,
  withStudentName,
  student,
}: Omit<FeeDisplayCardProps, 'onClick'>) => {
  return (
    <Stack gap="0">
      {withStudentName && student ? <FeeStudentName student={student} /> : null}
      <FeeDueDate dueAt={studentFee.fee.due_at} />
      <FeeOptional optional={studentFee.fee.optional} />
      <FeePartialPayment allowPartialPayment={studentFee.fee.allow_partial_payment} />
    </Stack>
  );
};

const FeeRightSection = ({ studentFee }: Omit<FeeDisplayCardProps, 'onClick'>) => {
  const { isStudentFeeInCart, feeTotal } = useIsFeeInCart({
    studentFeeId: studentFee.uuid,
    causeId: studentFee.cause.uuid,
  });

  const feeAmount = useMemo(() => {
    return centsToDollar(studentFee.amount_remaining);
  }, [studentFee.amount_remaining]);

  return (
    <Stack
      // Prevent any bubbling of the click event from children
      onClick={(event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      }}
      flex={0}
      align="flex-end"
      justify="space-between"
      style={{ alignSelf: 'stretch' }}
    >
      <Text size="sm" c="gray.8" fw={600}>
        {feeAmount}
      </Text>
      {isStudentFeeInCart ? (
        <ButtonMantine size="xs" leftSection={<IconCheck size={16} />} variant="outline">
          {centsToDollar(feeTotal)} Added
        </ButtonMantine>
      ) : (
        <AddFeeToCartButton studentFee={studentFee} />
      )}
    </Stack>
  );
};

const FeeDisplayCard = ({ studentFee, student, onClick, withStudentName }: FeeDisplayCardProps) => {
  const handleOnClick = useCallback(() => {
    onClick(studentFee.uuid);
  }, [onClick, studentFee.uuid]);

  if (!studentFee.fee) return null;
  return (
    <ClickableOptionsCard.Row
      groupProps={{ grow: true }}
      leftSection={<FeeImage icon={studentFee.fee.icon} />}
      title={studentFee.fee.name}
      subtitle={
        <FeeInfo studentFee={studentFee} withStudentName={withStudentName} student={student} />
      }
      onClick={handleOnClick}
      rightSection={<FeeRightSection studentFee={studentFee} />}
    >
      <Divider size="xs" color="cobalt.0" />
    </ClickableOptionsCard.Row>
  );
};

export { FeeDisplayCard };
